import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  Card,
  Tag,
  Space,
  Typography,
  Input,
  Select,
  Row,
  Col,
  Button,
  Tooltip,
  Modal,
  Descriptions,
  DatePicker,
  message,
  Dropdown,
} from "antd";
import {
  SearchOutlined,
  DollarOutlined,
  ReloadOutlined,
  EyeOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import {
  getAdminWalletTransactions,
  getUserNames,
} from "../../api/parkpass/parkpassWalletTransactions";
import { useSelector } from "react-redux";
import { hasPermission } from "../../store/userSlice";

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const transactionTypeColors = {
  topup: "blue",
  purchase: "orange",
  fine: "red",
  subscription: "volcano",
  refund: "green",
  adjustment: "purple",
  revenue_credit: "cyan",
  system_settlement: "gold",
  token_pool_adjustment: "magenta",
};

const transactionTypeLabels = {
  topup: "Recharge",
  purchase: "Achat",
  fine: "Amende",
  subscription: "Abonnement",
  refund: "Remboursement",
  adjustment: "Ajustement",
  revenue_credit: "Crédit de revenu",
  system_settlement: "Règlement système",
  token_pool_adjustment: "Ajustement pool de jetons",
};

const statusColors = {
  pending: "orange",
  completed: "green",
  failed: "red",
  cancelled: "default",
};

const statusLabels = {
  pending: "En attente",
  completed: "Terminé",
  failed: "Échoué",
  cancelled: "Annulé",
};

const paymentMethodColors = {
  WALLET: "blue",
  CMI: "gold",
  FATOURATI: "magenta",
  wallet: "blue",
  cmi: "gold",
  fatourati: "magenta",
};

const paymentMethodLabels = {
  WALLET: "Portefeuille",
  CMI: "CMI",
  FATOURATI: "Fatourati",
  wallet: "Portefeuille",
  cmi: "CMI",
  fatourati: "Fatourati",
};

const PAYMENT_METHOD_OPTIONS = [
  { value: "WALLET", label: <Tag color={paymentMethodColors.WALLET}>Portefeuille</Tag> },
  { value: "CMI", label: <Tag color={paymentMethodColors.CMI}>CMI</Tag> },
  { value: "FATOURATI", label: <Tag color={paymentMethodColors.FATOURATI}>Fatourati</Tag> },
];

const CitizenTransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [pdfExporting, setPdfExporting] = useState(false);

  const canFilterAll = useSelector((state) =>
    hasPermission(state, "citizen_transaction", "filter-all")
  );

  const [filters, setFilters] = useState({
    transactionType: undefined,
    status: undefined,
    userId: undefined,
    paymentMethod: undefined,
    search: undefined,
  });
  const [dateRange, setDateRange] = useState([]);

  const workerPdfRef = useRef(null);
  const workerExcelRef = useRef(null);

  const user = useSelector((state) => state.user.user);


  useEffect(() => {
    fetchTransactions();
    fetchUsers();

    // Initialize Web Workers
    workerPdfRef.current = new Worker(
      new URL("../../workers/pdfWorker.js", import.meta.url)
    );
    workerExcelRef.current = new Worker(
      new URL("../../workers/excelWorker.js", import.meta.url)
    );

    workerPdfRef.current.onmessage = (e) => {
      const { status, blobURL, operation, filename, message: msg } = e.data;

      switch (status) {
        case "starting":
          setPdfExporting(true);
          break;
        case "generating":
          break;
        case "complete":
          if (operation === "download") {
            const link = document.createElement("a");
            link.href = blobURL;
            link.download = filename || "file.pdf";
            link.click();
            URL.revokeObjectURL(blobURL);
          } else if (operation === "open") {
            window.open(blobURL, "_blank");
          }
          setPdfExporting(false);
          break;
        case "error":
          message.error(`Erreur lors de la génération du PDF: ${msg}`);
          setPdfExporting(false);
          break;
        default:
          break;
      }
    };

    workerExcelRef.current.onmessage = (e) => {
      const { success, blobURL, filename, error } = e.data;

      if (success) {
        const link = document.createElement("a");
        link.href = blobURL;
        link.download = filename || "file.xlsx";
        link.click();
        URL.revokeObjectURL(blobURL);
      } else {
        message.error(`Erreur lors de la génération du fichier Excel: ${error}`);
      }
    };

    return () => {
      workerPdfRef.current?.terminate();
      workerExcelRef.current?.terminate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTransactions();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await getUserNames();
      setUsers(response || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchTransactions = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
    currentFilters = filters
  ) => {
    try {
      setLoading(true);
      const response = await getAdminWalletTransactions({
        page,
        pageSize,
        // If multiple transaction types are selected, serialize to comma-separated string
        transactionType: Array.isArray(currentFilters.transactionType)
          ? currentFilters.transactionType.join(",")
          : currentFilters.transactionType,
        status: currentFilters.status,
        userId: currentFilters.userId,
        paymentMethod: currentFilters.paymentMethod,
        search: currentFilters.search,
        date_from:
          dateRange && dateRange[0]
            ? dateRange[0].format("YYYY-MM-DD HH:mm:ss")
            : undefined,
        date_to:
          dateRange && dateRange[1]
            ? dateRange[1].format("YYYY-MM-DD HH:mm:ss")
            : undefined,
      });
      setTransactions(response.items);
      setPagination({
        current: response.page,
        pageSize: response.page_size,
        total: response.total,
        totalAmount: response.total_amount,
        totalCredited: response.total_credited,
        totalDebited: response.total_debited,
      });
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    fetchTransactions(newPagination.current, newPagination.pageSize, filters);
  };
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
  };

  const handleSearch = () => {
    fetchTransactions(1, pagination.pageSize, filters);
  };

  const handleReset = () => {
    const resetFilters = {
      transactionType: undefined,
      status: undefined,
      userId: undefined,
      paymentMethod: undefined,
      search: undefined,
    };
    setFilters(resetFilters);
    setDateRange([]);
    fetchTransactions(1, pagination.pageSize, resetFilters);
  };

  // --- Export Functions (Web Worker-based, matching TransactionPage skeleton) ---

  const formatCellDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleString("fr-FR");
  };

  const getPaymentMethodLabel = (method) => {
    if (!method) return "";
    return paymentMethodLabels[method] || method;
  };

  const fetchExportData = async () => {
    try {
      const response = await getAdminWalletTransactions({
        transactionType: Array.isArray(filters.transactionType)
          ? filters.transactionType.join(",")
          : filters.transactionType,
        status: filters.status,
        userId: filters.userId,
        paymentMethod: filters.paymentMethod,
        search: filters.search,
        date_from:
          dateRange && dateRange[0]
            ? dateRange[0].format("YYYY-MM-DD HH:mm:ss")
            : undefined,
        date_to:
          dateRange && dateRange[1]
            ? dateRange[1].format("YYYY-MM-DD HH:mm:ss")
            : undefined,
        no_pagination: true,
      });
      return Array.isArray(response) ? response : (response.items || []);
    } catch (error) {
      console.error("Error fetching export data:", error);
      message.error("Erreur lors de la récupération des données d'export");
      return null;
    }
  };

  const handlePdfClick = async (operation) => {
    setPdfExporting(true);
    const exportTransactions = await fetchExportData();
    if (!exportTransactions) {
      setPdfExporting(false);
      return;
    }

    const timestamp = new Date().toISOString().replace(/:/g, "-");
    const filename = `citizen_wallet_transactions_${timestamp}.pdf`;
    const baseUrl = window.location.origin;

    const formattedData = exportTransactions.map((tx) => ({
      transaction_id: tx.transaction_id || "",
      user_full_name:
        tx.user_full_name ||
        `${tx.user_first_name || ""} ${tx.user_last_name || ""}`.trim() ||
        "N/A",
      amount: tx.amount || 0,
      type: transactionTypeLabels[tx.type] || tx.type || "",
      status: statusLabels[tx.status] || tx.status || "",
      payment_method: getPaymentMethodLabel(tx.payment_method),
      payment_reference: tx.payment_reference || "",
      created_at: formatCellDate(tx.created_at),
      completed_at: formatCellDate(tx.completed_at),
    }));

    const data = [
      [
        "Transactions Citizen Wallet",
        "",
        `Date à partir de : ${dateRange[0]?.format("YYYY-MM-DD HH:mm:ss") || "N/A"}`,
        `Date jusqu'au : ${dateRange[1]?.format("YYYY-MM-DD HH:mm:ss") || "N/A"}`,
      ],
      [],
      [],
      user.full_name,
      [`Net: ${pagination.totalAmount} MAD \nCrédité: ${pagination.totalCredited} MAD\nDébité: ${pagination.totalDebited} MAD` ],
      [
        [
          { text: "ID", type: "string", key: "transaction_id" },
          { text: "Utilisateur", type: "string", key: "user_full_name" },
          { text: "Montant", type: "int", key: "amount", align: "right" },
          { text: "Type", type: "string", key: "type" },
          { text: "Statut", type: "string", key: "status" },
          { text: "Méthode", type: "string", key: "payment_method" },
          { text: "Référence", type: "string", key: "payment_reference" },
          { text: "Créé le", type: "string", key: "created_at" },
          { text: "Complété le", type: "string", key: "completed_at" },
        ],
      ],
      [formattedData],
      baseUrl,
      {
        total: formattedData.reduce(
          (sum, it) => sum + parseFloat(it.amount || 0),
          0
        ),
        label: "Total",
      },
    ];

    workerPdfRef.current.postMessage({ data, operation, filename });
  };

  const handleExcelClick = async () => {
    setPdfExporting(true);
    const exportTransactions = await fetchExportData();
    if (!exportTransactions) {
      setPdfExporting(false);
      return;
    }

    const timestamp = new Date().toISOString().replace(/:/g, "-");
    const filename = `citizen_wallet_transactions_${timestamp}.xlsx`;
    const baseUrl = window.location.origin;

    const formattedData = exportTransactions.map((tx) => ({
      transaction_id: tx.transaction_id || "",
      user_full_name:
        tx.user_full_name ||
        `${tx.user_first_name || ""} ${tx.user_last_name || ""}`.trim() ||
        "N/A",
      user_email: tx.user_email || "",
      amount: tx.amount || 0,
      type: transactionTypeLabels[tx.type] || tx.type || "",
      status: statusLabels[tx.status] || tx.status || "",
      payment_method: getPaymentMethodLabel(tx.payment_method),
      payment_reference: tx.payment_reference || "",
      created_at: formatCellDate(tx.created_at),
      completed_at: formatCellDate(tx.completed_at),
    }));

    const data = [
      [
        "Transactions Citizen Wallet",
        "Format Général",
        `Date à partir de : ${dateRange[0]?.format("YYYY-MM-DD HH:mm:ss") || "N/A"}`,
        `Date jusqu'au : ${dateRange[1]?.format("YYYY-MM-DD HH:mm:ss") || "N/A"}`,
      ],
      [],
      [],
      user.full_name,
      [
          `Net: ${pagination.totalAmount} MAD \nCrédité: ${pagination.totalCredited} MAD\nDébité: ${pagination.totalDebited} MAD` 
      ],
      [
        [
          { text: "ID", type: "string", key: "transaction_id" },
          { text: "Utilisateur", type: "string", key: "user_full_name" },
          { text: "Email", type: "string", key: "user_email" },
          { text: "Montant", type: "int", key: "amount" },
          { text: "Type", type: "string", key: "type" },
          { text: "Statut", type: "string", key: "status" },
          { text: "Méthode", type: "string", key: "payment_method" },
          { text: "Référence", type: "string", key: "payment_reference" },
          { text: "Créé le", type: "string", key: "created_at" },
          { text: "Complété le", type: "string", key: "completed_at" },
        ],
      ],
      [formattedData],
      baseUrl,
      {
        total: formattedData.reduce(
          (sum, it) => sum + parseFloat(it.amount || 0),
          0
        ),
        label: "Total",
      },
    ];

    workerExcelRef.current.postMessage({ data, filename });
    setPdfExporting(false);
  };

  const getPdfMenuItems = (handlePdfClick, pdfExporting) => [
    {
      key: "download",
      label: "Télécharger",
      onClick: () => handlePdfClick("download"),
      disabled: pdfExporting,
    },
    {
      key: "open",
      label: "Ouvrir",
      onClick: () => handlePdfClick("open"),
      disabled: pdfExporting,
    },
  ];

  // --- End Export Functions ---

  const handleViewDetail = (record) => {
    setSelectedTransaction(record);
    setDetailModalVisible(true);
  };

  const handleCloseDetail = () => {
    setDetailModalVisible(false);
    setSelectedTransaction(null);
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "transaction_id",
      key: "transaction_id",
      width: 80,
    },
    {
      title: "Utilisateur",
      key: "user",
      width: 220,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {record.user_full_name ||
              `${record.user_first_name || ""} ${
                record.user_last_name || ""
              }`.trim() ||
              "N/A"}
          </div>
          {record.user_email && (
            <div style={{ fontSize: 12, color: "#999" }}>
              {record.user_email}
            </div>
          )}
          <div style={{ fontSize: 11, color: "#bbb" }}>
            User ID: {record.user_id} | Wallet ID: {record.wallet_id}
          </div>
        </div>
      ),
    },
    {
      title: "Montant",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      render: (amount, record) => (
        <div
          style={{
            fontWeight: 600,
            color: record.amount < 0 ? "#f5222d" : "#52c41a",
          }}
        >
          {amount} MAD
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 120,
      render: (type) => (
        <Tag color={transactionTypeColors[type]}>
          {transactionTypeLabels[type] || type}
        </Tag>
      ),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <Tag color={statusColors[status]}>{statusLabels[status] || status}</Tag>
      ),
    },
    {
      title: "Méthode",
      dataIndex: "payment_method",
      key: "payment_method",
      width: 120,
      render: (method) =>
        method ? (
          <Tag color={paymentMethodColors[method] || "default"}>
            {paymentMethodLabels[method] || method}
          </Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span>{text || "N/A"}</span>
        </Tooltip>
      ),
    },
    {
      title: "Référence",
      dataIndex: "payment_reference",
      key: "payment_reference",
      width: 150,
      render: (ref) => (
        <span style={{ fontFamily: "monospace", fontSize: 12 }}>
          {ref || "N/A"}
        </span>
      ),
    },
    {
      title: "Date de création",
      dataIndex: "created_at",
      key: "created_at",
      width: 160,
      render: (date) => new Date(date).toLocaleString("fr-FR"),
    },
    {
      title: "Date de fin",
      dataIndex: "completed_at",
      key: "completed_at",
      width: 160,
      render: (date) => (date ? new Date(date).toLocaleString("fr-FR") : "N/A"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          size="small"
          onClick={() => handleViewDetail(record)}
        >
          Détails
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <DollarOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Transactions Citizen Wallet
            </Title>
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: "100%", marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Rechercher par ID, Description, Référence, Clé d'idempotence..."
                prefix={<SearchOutlined />}
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                onPressEnter={handleSearch}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <RangePicker
                placeholder={["Date de début", "Date de fin"]}
                style={{ width: "100%" }}
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
                showTime
                format="YYYY-MM-DD HH:mm"
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                mode="multiple"
                placeholder="Type de transaction"
                style={{ width: "100%" }}
                value={filters.transactionType}
                onChange={(value) =>
                  handleFilterChange("transactionType", value)
                }
                allowClear
              >
                <Option value="topup">
                  <Tag color={transactionTypeColors.topup}>
                    {transactionTypeLabels.topup}
                  </Tag>
                </Option>
                <Option value="purchase">
                  <Tag color={transactionTypeColors.purchase}>
                    {transactionTypeLabels.purchase}
                  </Tag>
                </Option>
                <Option value="fine">
                  <Tag color={transactionTypeColors.fine}>
                    {transactionTypeLabels.fine}
                  </Tag>
                </Option>
                <Option value="subscription">
                  <Tag color={transactionTypeColors.subscription}>
                    {transactionTypeLabels.subscription}
                  </Tag>
                </Option>
                {canFilterAll && (
                  <>
                    <Option value="refund">
                      <Tag color={transactionTypeColors.refund}>
                        {transactionTypeLabels.refund}
                      </Tag>
                    </Option>
                    <Option value="adjustment">
                      <Tag color={transactionTypeColors.adjustment}>
                        {transactionTypeLabels.adjustment}
                      </Tag>
                    </Option>
                    <Option value="revenue_credit">
                      <Tag color={transactionTypeColors.revenue_credit}>
                        {transactionTypeLabels.revenue_credit}
                      </Tag>
                    </Option>
                    <Option value="system_settlement">
                      <Tag color={transactionTypeColors.system_settlement}>
                        {transactionTypeLabels.system_settlement}
                      </Tag>
                    </Option>
                    <Option value="token_pool_adjustment">
                      <Tag color={transactionTypeColors.token_pool_adjustment}>
                        {transactionTypeLabels.token_pool_adjustment}
                      </Tag>
                    </Option>
                  </>
                )}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder="Statut"
                style={{ width: "100%" }}
                value={filters.status}
                onChange={(value) => handleFilterChange("status", value)}
                allowClear
              >
                <Option value="pending">
                  <Tag color={statusColors.pending}>{statusLabels.pending}</Tag>
                </Option>
                <Option value="completed">
                  <Tag color={statusColors.completed}>
                    {statusLabels.completed}
                  </Tag>
                </Option>
                <Option value="failed">
                  <Tag color={statusColors.failed}>{statusLabels.failed}</Tag>
                </Option>
                <Option value="cancelled">
                  <Tag color={statusColors.cancelled}>
                    {statusLabels.cancelled}
                  </Tag>
                </Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder="Méthode de paiement"
                style={{ width: "100%" }}
                value={filters.paymentMethod}
                onChange={(value) => handleFilterChange("paymentMethod", value)}
                allowClear
              >
                {PAYMENT_METHOD_OPTIONS.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder="Sélectionner un utilisateur"
                style={{ width: "100%" }}
                value={filters.userId}
                onChange={(value) => handleFilterChange("userId", value)}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {users.map((user) => (
                  <Option key={user.user_id} value={user.user_id}>
                    {user.full_name ||
                      `${user.first_name || ""} ${
                        user.last_name || ""
                      }`.trim() ||
                      user.email ||
                      `User ${user.user_id}`}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                >
                  Rechercher
                </Button>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  Réinitialiser
                </Button>
              </Space>
            </Col>
          </Row>
        </Space>
        <div style={{ marginBottom: 16, gap: 8 }} className="flex justify-end">
          <Button
            icon={<FileExcelOutlined />}
            onClick={handleExcelClick}
            disabled={transactions.length === 0 || pdfExporting}
          >
            Export Excel
          </Button>
          <Dropdown
            menu={{
              items: getPdfMenuItems(handlePdfClick, pdfExporting),
            }}
            disabled={pdfExporting}
          >
            <Button
              icon={<FilePdfOutlined />}
              disabled={pdfExporting}
            >
              Export PDF{" "}
              {pdfExporting ? (
                "..."
              ) : (
                <span style={{ marginLeft: 5 }}>▼</span>
              )}
            </Button>
          </Dropdown>
        </div>
        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="transaction_id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => {
              const formattedAmount = new Intl.NumberFormat("fr-FR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(pagination.totalAmount || 0);
              const formattedCredited = new Intl.NumberFormat("fr-FR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(pagination.totalCredited || 0);
              const formattedDebited = new Intl.NumberFormat("fr-FR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(pagination.totalDebited || 0);

              return (
                <span>
                  {`Total: ${total} transactions | `}
                  <strong>
                    Net: {formattedAmount} MAD | 
                    Crédité: {formattedCredited} MAD | 
                    Débité: {formattedDebited} MAD
                  </strong>
                </span>
              );
            },
            pageSizeOptions: ["10", "25", "50", "100"],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1500 }}
        />
      </Card>

      <Modal
        title={
          <Space>
            <EyeOutlined />
            <span>Détails de la Transaction</span>
          </Space>
        }
        open={detailModalVisible}
        onCancel={handleCloseDetail}
        footer={[
          <Button key="close" type="primary" onClick={handleCloseDetail}>
            Fermer
          </Button>,
        ]}
        width={800}
      >
        {selectedTransaction && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="ID Transaction">
              <strong>{selectedTransaction.transaction_id}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="ID Wallet">
              {selectedTransaction.wallet_id}
            </Descriptions.Item>
            <Descriptions.Item label="ID Utilisateur">
              {selectedTransaction.user_id}
            </Descriptions.Item>
            <Descriptions.Item label="Nom complet">
              {selectedTransaction.user_full_name ||
                `${selectedTransaction.user_first_name || ""} ${
                  selectedTransaction.user_last_name || ""
                }`.trim() ||
                "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {selectedTransaction.user_email || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Montant">
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 16,
                  color: selectedTransaction.amount < 0 ? "#f5222d" : "#52c41a",
                }}
              >
                {selectedTransaction.amount} MAD
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Type">
              <Tag color={transactionTypeColors[selectedTransaction.type]}>
                {transactionTypeLabels[selectedTransaction.type] ||
                  selectedTransaction.type}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Statut">
              <Tag color={statusColors[selectedTransaction.status]}>
                {statusLabels[selectedTransaction.status] ||
                  selectedTransaction.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Méthode de paiement">
              {selectedTransaction.payment_method ? (
                <Tag color={paymentMethodColors[selectedTransaction.payment_method] || "default"}>
                  {paymentMethodLabels[selectedTransaction.payment_method] ||
                    selectedTransaction.payment_method}
                </Tag>
              ) : (
                "-"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Description">
              {selectedTransaction.description || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Référence de paiement">
              <span style={{ fontFamily: "monospace" }}>
                {selectedTransaction.payment_reference || "N/A"}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Clé d'idempotence">
              <span style={{ fontFamily: "monospace", fontSize: 11 }}>
                {selectedTransaction.idempotency_key || "N/A"}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Métadonnées">
              <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                {selectedTransaction.transaction_metadata || "N/A"}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="ID Transaction Liée">
              {selectedTransaction.related_transaction_id || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="ID System Wallet">
              {selectedTransaction.system_wallet_id || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Date de création">
              {new Date(selectedTransaction.created_at).toLocaleString(
                "fr-FR",
                {
                  dateStyle: "full",
                  timeStyle: "long",
                }
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Date de complétion">
              {selectedTransaction.completed_at
                ? new Date(selectedTransaction.completed_at).toLocaleString(
                    "fr-FR",
                    {
                      dateStyle: "full",
                      timeStyle: "long",
                    }
                  )
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Date de mise à jour">
              {new Date(selectedTransaction.updated_at).toLocaleString(
                "fr-FR",
                {
                  dateStyle: "full",
                  timeStyle: "long",
                }
              )}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default CitizenTransactionsPage;
